import { buildModel, currentStreak, type DateKey, type PuzzleModel, summarizeStats } from '@arrow/game-engine';
import type { Level } from '@arrow/puzzle-schema';
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, type DailyLevels, type DailyPuzzle, fetchDaily, fetchDailyLevels, type LoadError } from '../api/client.ts';
import { cacheDaily, cachedDaily, cachedLevels, cacheLevels, setHandoff } from '../app/handoff.ts';
import { setPageTitle } from '../app/page-title.ts';
import type { Prefs } from '../app/prefs.ts';
import { navigate } from '../app/router.tsx';
import { boardStructure, structureModel } from '../app/structure.ts';
import { HeroBoard, type HeroHandle } from '../components/attract/HeroBoard.tsx';
import { AppMenu } from '../components/AppMenu.tsx';
import { BoardMap } from '../components/BoardMap.tsx';
import { BrandRow } from '../components/BrandRow.tsx';
import { ChallengeFeedback } from '../components/ChallengeFeedback.tsx';
import { MoveNotice } from '../components/MoveNotice.tsx';
import { useTodayInstall } from '../components/install/TodayInstall.tsx';
import { Wedge } from '../components/icons.tsx';
import { Nib } from '../components/Nib.tsx';
import { ErrorState, Pending, Skeleton } from '../components/States.tsx';
import { TabBar } from '../components/TabBar.tsx';
import { prefersReducedMotion } from '../game/haptics.ts';
import { forgetLeftDaily, leftDaily } from '../game/recent.ts';
import { dayMonth, weekdayKey, weekdayName } from '../game/view.ts';
import { type Persistence, type ResumeInfo, slotFor } from '../storage/persistence.ts';
import { RadioGroup, RadioGroupItem } from '../ui/index.ts';
import { allDone, initialLevel, LEVEL_NAME, type Note, noteFor, pillFor, untilMidnight } from './today-model.ts';
import '../styles/today.css';

interface TodayProps {
	todayKey: DateKey;
	persistence: Persistence;
	prefs: Prefs;
	/** No play history on this device and no level remembered. */
	firstVisit: boolean;
	dev?: { message: boolean; back?: { label: string; onClick: () => void } | undefined } | undefined;
}

type Load =
	| { status: 'loading' }
	| { status: 'error'; error: LoadError }
	| { status: 'ready'; levels: DailyLevels; dailies: Partial<Record<Level, DailyPuzzle>> };

/** Loads the day's levels and each level's puzzle (from the cache when Today was just here). */
async function loadDay(todayKey: DateKey, signal: AbortSignal): Promise<Load> {
	const levels = cachedLevels(todayKey) ?? (await fetchDailyLevels(todayKey, signal));
	cacheLevels(levels);
	const dailies: Partial<Record<Level, DailyPuzzle>> = {};
	let firstError: unknown = null;
	await Promise.all(
		levels.levels.map(async ({ level }) => {
			try {
				const d = cachedDaily({ date: todayKey }, level) ?? (await fetchDaily(todayKey, level, signal));
				cacheDaily(d);
				dailies[level] = d;
			} catch (err) {
				firstError ??= err;
			}
		}),
	);
	if (Object.keys(dailies).length === 0) throw firstError ?? new ApiError({ kind: 'not-found', message: 'No levels today.' });
	return { status: 'ready', levels, dailies };
}

/**
 * Today (option B, "Attract"): the brand row with the streak, the day ("Thursday") over its
 * number and date, today's board playing itself, the levels as little printed
 * formes, one line of guidance and the one big action.
 */
export function Today({ todayKey, persistence, prefs, firstVisit, dev }: TodayProps) {
	const [load, setLoad] = useState<Load>({ status: 'loading' });
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		const ctrl = new AbortController();
		setLoad({ status: 'loading' });
		loadDay(todayKey, ctrl.signal).then(setLoad, (err: unknown) => {
			if (ctrl.signal.aborted) return;
			const error: LoadError =
				err instanceof ApiError ? err.detail : { kind: 'network', message: 'Something went wrong loading the puzzle.' };
			if (error.kind === 'invalid' && 'issues' in error) console.error('Puzzle failed validation', error.issues);
			setLoad({ status: 'error', error });
		});
		return () => ctrl.abort();
	}, [todayKey, attempt]);

	if (load.status === 'loading') {
		// The board it's likely to open on, for the square's size (the hero's own cell cap).
		const guess = prefs.lastLevel() ?? (firstVisit ? 'easy' : 'medium');
		return (
			<TodayFrame todayKey={todayKey} persistence={persistence}>
				<TodayTitle todayKey={todayKey} number={null} loading />
				{/* A quick load shows nothing; a slow one, the shape of the page to come (UX-015),
				    laid out in the page's own boxes, so nothing moves when it arrives (QA4-4). */}
				<Pending label="Loading today’s Sparrow">
					<div className="today-play today-skel" aria-hidden="true">
						<div className="hero-slot">
							<div className="hero-stage">
								<Skeleton className="today-skel-board" style={{ '--cols': SKELETON_SIDE[guess] } as CSSProperties} />
							</div>
						</div>
						<div className="today-side">
							<div className="formes" data-n="3">
								{[7, 8, 11].map((side) => (
									<div className="forme" key={side} style={{ '--mw': `${formeSize(side)}px` } as CSSProperties}>
										<Skeleton className="today-skel-forme" />
										<Skeleton className="today-skel-label" />
									</div>
								))}
							</div>
							<p className="note">
								<Skeleton className="today-skel-line" />
							</p>
							<Skeleton className="today-skel-pill" />
						</div>
					</div>
				</Pending>
			</TodayFrame>
		);
	}
	if (load.status === 'error')
		return (
			<TodayFrame todayKey={todayKey} persistence={persistence}>
				<TodayTitle todayKey={todayKey} number={null} />
				{/* In the page, so the tabs, Stats and ≡ stay a tap away (UX-016). */}
				<ErrorState error={load.error} onRetry={() => setAttempt((n) => n + 1)} dev={dev} inPage game />
			</TodayFrame>
		);
	return <TodayReady todayKey={todayKey} persistence={persistence} prefs={prefs} firstVisit={firstVisit} data={load} />;
}

/** Today's shell while it loads or can't: the brand row with the streak, ≡, and the tab bar. */
function TodayFrame({ todayKey, persistence, children }: { todayKey: DateKey; persistence: Persistence; children: ReactNode }) {
	const streak = currentStreak(persistence.loadStats(), todayKey);
	return (
		<div className="page today" data-weekday={weekdayKey(todayKey)}>
			{/* The brand row is the page's banner, outside <main> (QA4-13). */}
			<div className="page-body">
				<BrandRow streak={streak} after={<AppMenu screen="today" install={{ todayKey }} />} />
				<main className="page-main">{children}</main>
			</div>
			<TabBar current="today" />
		</div>
	);
}

/**
 * The day as the page's title, at the tabs' title tier (J8), over one quiet line, "28 October,
 * No. 4", as the game's masthead and the poster set it (the number waits for the day's puzzles).
 */
function TodayTitle({
	todayKey,
	number,
	loading = false,
	className,
	style,
}: {
	todayKey: DateKey;
	number: number | null;
	loading?: boolean;
	className?: string;
	style?: CSSProperties;
}) {
	return (
		<div className={className ? `title ${className}` : 'title'} style={style}>
			<h1 className="ttl">{weekdayName(todayKey)}</h1>
			<p className="sub">
				{dayMonth(todayKey)}
				{number !== null ? `, No. ${number}` : loading ? <Skeleton className="today-skel-no" /> : null}
			</p>
		</div>
	);
}

interface LevelView {
	level: Level;
	daily: DailyPuzzle;
	model: PuzzleModel;
	slot: string;
	resume: ResumeInfo;
	solvedAnswers: string[];
	segments: string[][];
}

function TodayReady({
	todayKey,
	persistence,
	prefs,
	firstVisit,
	data,
}: Omit<TodayProps, 'dev'> & { data: Extract<Load, { status: 'ready' }> }) {
	const views = useMemo<LevelView[]>(
		() =>
			data.levels.levels.flatMap(({ level }) => {
				const daily = data.dailies[level];
				if (!daily) return [];
				const model = buildModel(daily.puzzle);
				const slot = slotFor({ kind: 'daily', date: daily.date, level });
				const saved = persistence.loadGame(model, slot);
				return [
					{
						level,
						daily,
						model,
						slot,
						resume: persistence.resumeInfo(slot),
						solvedAnswers: saved?.solvedAnswers ?? [],
						segments: persistence.loadFlow(slot).segments,
					},
				];
			}),
		[data, persistence],
	);
	const remembered = prefs.lastLevel();
	// The level it opens on: yours, or the next one once yours is solved (QA2-9), unless
	// you've just come back from it. The trip back is spent once Today closes.
	const [opened] = useState<Level>(() =>
		initialLevel(
			views.map((v) => v.level),
			{
				remembered,
				firstVisit,
				started: views.filter((v) => v.resume.status === 'started').map((v) => v.level),
				levels: views.map((v) => ({ level: v.level, done: v.resume.status === 'completed' })),
				justLeft: leftDaily(todayKey),
			},
		),
	);
	useEffect(() => () => forgetLeftDaily(), []);
	const [level, setLevel] = useState<Level>(opened);
	const view = views.find((v) => v.level === level) ?? views[0]!;
	// Opened on the next level because yours is solved: the note says so, with its result.
	const yours = remembered && opened !== remembered ? views.find((v) => v.level === remembered && v.resume.status === 'completed') : undefined;
	const after = yours && level === opened ? { level: yours.level, elapsedMs: yours.resume.elapsedMs } : null;
	const still = useMemo(() => prefersReducedMotion(), []);
	const [leaving, setLeaving] = useState(false);
	const heroRef = useRef<HeroHandle>(null);
	useEffect(() => setPageTitle(), []);
	const ctaRef = useRef<HTMLButtonElement>(null);
	const introRef = useRef(true);
	useEffect(() => {
		introRef.current = false;
	}, []);

	// Structure only: the hero never sees a letter (app/structure.ts).
	const structure = useMemo(() => boardStructure(view.model, view.solvedAnswers), [view]);
	const formes = useMemo(() => views.map((v) => ({ level: v.level, map: structureModel(v.model) })), [views]);
	const solved = view.resume.status === 'completed';
	const route = useMemo(() => longest(view.segments), [view]);

	const stats = persistence.loadStats();
	const streak = currentStreak(stats, todayKey);
	// Never mid-game: the card and the desktop button live on Today alone (the menu's line is AppMenu's).
	const dailiesCompleted = summarizeStats(stats, todayKey).dailies.completed;
	const install = useTodayInstall({ todayKey, dailiesCompleted });
	const onNotice = () => install.onNotice();
	const number = data.levels.number;
	const dayLevels = views.map((v) => ({ level: v.level, done: v.resume.status === 'completed' }));
	// The day's done: when the next one's out, ticking by the minute (plain text, not a live region).
	const finished = allDone(dayLevels);
	const now = useMinute(finished);
	const note = noteFor(level, {
		resume: view.resume,
		firstVisit,
		remembered,
		levels: dayLevels,
		untilNext: finished ? untilMidnight(now) : null,
		after,
	});
	const pill = pillFor(view.resume);
	// Something under the pill (the install line): the hero keeps its size and the page
	// scrolls, rather than the board shrinking to make room (UX-029).
	const extra = install.card ?? null;

	/** Into `target`'s game (the pill), or straight to its result (the note's "See result"). */
	const open = (target: LevelView, result: boolean) => {
		if (leaving) return;
		const ghost = heroRef.current?.ghost() ?? null;
		const handOver = () => {
			setHandoff({
				slot: target.slot,
				rect: heroRef.current?.rect() ?? null,
				ghost: !result && target.resume.status === 'new' ? ghost : null,
				openResult: result,
				at: Date.now(),
			});
			// A pick on Today is your level; a look at an earlier result isn't.
			if (!result || target.level === level) prefs.setLastLevel(target.level);
			navigate(`/daily/${number}?l=${target.level}`);
		};
		const btn = ctaRef.current;
		btn?.classList.add('down');
		window.setTimeout(() => btn?.classList.remove('down'), 110);
		if (still) return handOver();
		setLeaving(true);
		heroRef.current?.freeze();
		window.setTimeout(handOver, 90);
	};
	const go = () => open(view, pill.kind === 'result');

	const intro = introRef.current && !still;
	const fade = (i: number) => (intro ? ({ className: 'fadein', style: { '--rd': `${i * 60}ms` } as CSSProperties }) : {});
	const out = (i: number) => ({ '--od': `${i * 30}ms` }) as CSSProperties;
	// One level today: nothing to choose, so its forme is a label, not a toggle.
	const choose = views.length > 1;

	return (
		<div className={leaving ? 'page today leaving' : 'page today'} data-weekday={weekdayKey(todayKey)} data-extra={extra ? '' : undefined}>
			<div className={extra ? 'page-body page-scroll' : 'page-body'}>
				<BrandRow
					streak={streak}
					className="out-up"
					before={install.before}
					after={<AppMenu screen="today" install={{ todayKey }} />}
				/>
				<main className="page-main">
					{install.top && (
						<div className="out-up" data-slot="top">
							{install.top}
						</div>
					)}
					<TodayTitle todayKey={todayKey} number={number} className={`out ${fade(0).className ?? ''}`} style={{ ...fade(0).style, ...out(0) }} />
					<MoveNotice onShow={onNotice} />
					<ChallengeFeedback todayKey={todayKey} onShow={onNotice} />
					{/* Phones: one column (these wrappers are `display: contents`). Desktop: the board on
					    the left, the levels, guidance and action beside it (screens.css). */}
					<div className="today-play">
						<HeroBoard
							ref={heroRef}
							structure={structure}
							mode={solved ? 'solved' : 'attract'}
							route={route}
							still={still}
							intro={!still}
							label={`Today’s ${view.model.width} by ${view.model.height} board, without clues or letters`}
							perch={<Nib pose="idle" className="hero-nib" blink={!still} type={view.daily.puzzle.type} />}
						/>
						<div className="today-side">
							{/* Two or three levels: a radiogroup (ui/RadioGroup), one Tab stop, the arrows
							    choosing. One level: nothing to choose, so its forme is a caption. */}
							{choose ? (
								<RadioGroup
									className={`formes out ${fade(1).className ?? ''}`}
									style={{ ...fade(1).style, ...out(1) }}
									label="Level"
									value={level}
									onValueChange={setLevel}
									data-n={views.length}
								>
									{views.map((v) => (
										<RadioGroupItem key={v.level} value={v.level} className="forme" tick={false} style={{ '--mw': `${formeSize(Math.max(v.model.width, v.model.height))}px` } as CSSProperties}>
											<FormeFace view={v} map={formes.find((f) => f.level === v.level)!.map} />
										</RadioGroupItem>
									))}
								</RadioGroup>
							) : (
								<div className={`formes out ${fade(1).className ?? ''}`} style={{ ...fade(1).style, ...out(1) }} role="group" aria-label="Level" data-n={views.length}>
									{views.map((v) => (
										<div key={v.level} className="forme" data-only="">
											<FormeFace view={v} />
										</div>
									))}
								</div>
							)}
							<p className={`note out ${fade(2).className ?? ''}`} style={{ ...fade(2).style, ...out(2) }} data-note={note.kind}>
								<NoteMark note={note} />
								<span className="nt">
									{note.text}
									{note.kind === 'solved' && note.next ? (
										<span className="q">
											{' · '}
											{/* The suggestion is the way there: it picks that level (UX-030). */}
											<button type="button" className="note-go" onClick={() => setLevel(note.next!)}>
												Try {LEVEL_NAME[note.next]} next?
											</button>
										</span>
									) : note.kind === 'after' && yours ? (
										<span className="q">
											{' · '}
											<button type="button" className="note-go" onClick={() => open(yours, true)} aria-label={`See your ${LEVEL_NAME[note.done]} result`}>
												See result
											</button>
										</span>
									) : (
										note.quiet && <span className="q">{note.quiet}</span>
									)}
								</span>
							</p>
							<button
								ref={ctaRef}
								type="button"
								className={`btn lg primary today-cta out ${fade(3).className ?? ''}`}
								style={{ ...fade(3).style, ...out(3) }}
								onClick={go}
								// The level is in the name, so it isn't confused with the Play tab (UX-025).
								aria-label={pill.kind === 'result' ? undefined : `${pill.kind === 'play' ? 'Play' : 'Continue'} ${LEVEL_NAME[level]}`}
							>
								{/* The time so far is on the note line just above: not repeated here. */}
								{pill.kind === 'play' ? 'Play' : pill.kind === 'result' ? 'See your result' : 'Continue'}
								<Wedge direction="right" />
							</button>
							{extra && (
								<div className="out" style={out(4)}>
									{extra}
								</div>
							)}
						</div>
					</div>
				</main>
			</div>
			<div className="out" style={out(5)}>
				<TabBar current="today" />
			</div>
			{install.sheets}
		</div>
	);
}

/** Now, refreshed every half minute while `on` (the countdown to the next daily). */
function useMinute(on: boolean): Date {
	const [now, setNow] = useState(() => new Date());
	useEffect(() => {
		if (!on) return;
		setNow(new Date());
		const id = window.setInterval(() => setNow(new Date()), 30_000);
		return () => window.clearInterval(id);
	}, [on]);
	return now;
}

/** The first visit's wedge. Started and solved have no mark here: the level's forme above has it. */
function NoteMark({ note }: { note: Note }) {
	if (note.kind === 'first') return <Wedge direction="diagonal" className="nm" />;
	return null;
}

function Tick() {
	return (
		<span className="tick" aria-hidden="true">
			<svg viewBox="0 0 16 16">
				<path d="M3.5 8.5l3 3 6-7" />
			</svg>
		</span>
	);
}

/**
 * A level's forme: its thumbnail and name (the thumbnail shows its size). Given no `map` (one level today), the hero
 * above is its board, so no thumbnail, and the note line under it already says where you
 * are, so no tick or ring either.
 */
function FormeFace({ view, map }: { view: LevelView; map?: PuzzleModel }) {
	const status = view.resume.status;
	return (
		<>
			{map && <BoardMap model={map} />}
			<span className="lb">
				<span className="n">{LEVEL_NAME[view.level]}</span>
				{map && status === 'completed' && <Tick />}
				{/* Started: a quiet ink ring after the name, never the selection's vermilion (QA1-11). */}
				{map && status === 'started' && <span className="prog" />}
				{status !== 'new' && <span className="sr-only">{status === 'completed' ? ', solved' : ', started'}</span>}
			</span>
		</>
	);
}

/** A level's thumbnail (px), by its board's side: 7×7 is 50, 11×11 is 72. */
function formeSize(side: number): number {
	return Math.round(Math.max(44, Math.min(76, 50 + (side - 7) * 5.5)));
}

/** The usual board side at each level, for the loading square. */
const SKELETON_SIDE: Record<Level, number> = { easy: 7, medium: 8, hard: 11 };

function longest(segments: readonly string[][]): string[] {
	let best: string[] = [];
	for (const s of segments) if (s.length > best.length) best = s;
	return best;
}
